import { IonAlert, IonButton, IonButtons, IonCol, IonContent, IonGrid, IonHeader, IonIcon, IonInput, IonItem, IonLabel, IonList, IonLoading, IonNote, IonPage, IonRow, IonText, IonTitle, IonToolbar, useIonToast } from "@ionic/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTenant } from "../hooks/multiTenancy";
import { getNonFidelityAppSalesPoint, getPrepaidAccountBalance, getPrice, roundForCurrency } from "../utils";
import { close as ci } from "ionicons/icons";
import "./splitPayments.css";
import { useAuth } from "../hooks/auth";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { ApolloClient, NormalizedCacheObject, useApolloClient, useMutation, useQuery, useReactiveVar } from "@apollo/client";
import { loginWithEmailMutation, logoutUserMutation, userQuery } from "../apollo-client/queries";
import { idAppCustomerDeviceVar, idFidelityIdentifierVar } from "../pages/auth/login";
import { eye, eyeOff } from "ionicons/icons";

type ModalProps = {
  isTotalAmount: boolean;
  totalAmount: number;
  paymentWithCredit: boolean;
  idSalesPoint: number;
  onSelect: (paymentName: string, paymentAmount: number) => void;
  close: () => void;
};

type LoginFormData = {
  email: string;
  password: string;
};

const schema = yup.object().shape({
  email: yup.string().email().required(),
  password: yup.string().required(),
});

const SplitPaymentsModal: React.FC<ModalProps> = ({ isTotalAmount, totalAmount, paymentWithCredit, idSalesPoint, onSelect, close }) => {
  const { t } = useTranslation();
  const [paymentName, setPaymentName] = useState<string>();
  const [maxPaymentAmount, setMaxPaymentAmount] = useState(totalAmount);
  const [paymentAmount, setPaymentAmount] = useState(isTotalAmount ? totalAmount : undefined);
  const [creditAmount, setCreditAmount] = useState<number>();
  const [load, setLoad] = useState(false);
  const tenant = useTenant();
  const [toast] = useIonToast();
  const [auth, setToken, , simpleLogout] = useAuth();
  const [loginMutation] = useMutation(loginWithEmailMutation);
  const selectedSalesPoint = getNonFidelityAppSalesPoint(tenant, idSalesPoint);
  const [logoutUser] = useMutation(logoutUserMutation);
  const apolloClient = useApolloClient() as ApolloClient<NormalizedCacheObject>;
  const amountInput = useRef<HTMLIonInputElement>(null);
  const [showNotEnoughCreditAlert, setShowNotEnoughCreditAlert] = useState(false);

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: yupResolver(schema),
    reValidateMode: "onChange",
  });

  const userResponse = useQuery(userQuery, { skip: !auth });
  const [showPassword, setShowPassword] = useState(false);

  const idAppCustomerDevice = useReactiveVar(idAppCustomerDeviceVar);

  const onSubmit = async (data: LoginFormData) => {
    try {
      const res = await loginMutation({ variables: data });
      setToken(res.data.login.accessToken.token);
      idAppCustomerDeviceVar(res.data.login.appCustomerDevice.id);
      idFidelityIdentifierVar(res.data.login.appCustomerDevice.idFidelityIdentifier);
    } catch (e) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const error = e as any;
      if (error.networkError) {
        if (error.networkError?.statusCode !== 200 && error.networkError.result && error.networkError.result.errorMsg) {
          const message = error.networkError.result.errorMsg;
          toast(message, 5000);
        } else {
          toast(t("auth.wrong_password"), 5000);
        }
      } else {
        toast("error", 5000);
      }
    }
  };

  const formatAmount = (v: string | null | undefined) => {
    const value = v ? Number(v) : undefined;
    if (value && value > 0) {
      const amount = roundForCurrency(value, tenant.settings?.fidelityAccount?.account.currency);
      const resultAmount = Math.min(maxPaymentAmount, amount);
      setPaymentAmount(resultAmount);
      if (amountInput.current) {
        amountInput.current.value = resultAmount;
      }
    } else if (value === 0) {
      setPaymentAmount(undefined);
      if (amountInput.current) {
        amountInput.current.value = value;
      }
    } else {
      setPaymentAmount(undefined);
      if (amountInput.current) {
        amountInput.current.value = undefined;
      }
    }
  };

  const confirm = useCallback(async () => {
    if (paymentName && paymentAmount) {
      setLoad(true);
      onSelect(paymentName, paymentAmount);
    } else {
      toast(t("split_payments.name_and_amount"), 5000);
    }
  }, [paymentAmount, paymentName]);

  const logoutAndRedirect = useCallback(() => {
    logoutUser({ variables: { idAppCustomerDevice } });
    simpleLogout(apolloClient);
  }, [simpleLogout, logoutUser, idAppCustomerDevice]);

  useEffect(() => {
    if (paymentWithCredit && auth.authenticated && userResponse.data) {
      const user = userResponse.data?.user;
      if (user && selectedSalesPoint) {
        setPaymentName(user.customer.name);
        const balance = getPrepaidAccountBalance(user, selectedSalesPoint.id);
        if (balance) {
          setCreditAmount(balance);
          if (balance < totalAmount) {
            setMaxPaymentAmount(balance);
            if (isTotalAmount) {
              setPaymentAmount(balance);
              setShowNotEnoughCreditAlert(true);
            } else {
              setPaymentAmount(undefined);
            }
          }
        }
      }
    }
  }, [paymentWithCredit, totalAmount, isTotalAmount, auth.authenticated, userResponse.data, selectedSalesPoint, setPaymentName, getPrepaidAccountBalance, setPaymentAmount, setCreditAmount, setMaxPaymentAmount, setPaymentAmount, setShowNotEnoughCreditAlert]);

  return (
    <IonPage className="splitContainer">
      <IonHeader>
        <IonToolbar color="secondary">
          <IonTitle>{isTotalAmount ? t("payment_types.CN_CREDIT") : t("table_payment.splitPay")}</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={() => close()}>
              <IonIcon icon={ci} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonLoading isOpen={load} message={t("loading")} />
      <IonContent className="splitContent">
        {paymentWithCredit && auth.authenticated && (creditAmount == 0 || creditAmount == undefined) && (
          <IonList lines="none" className="splitList">
            <IonItem>
              <IonText>{t("split_payments.not_enough_credit")}</IonText>
            </IonItem>
            <IonButton onClick={logoutAndRedirect} expand="block">
              {t("table_payment.changeUser")}
            </IonButton>
          </IonList>
        )}
        {paymentWithCredit && auth.authenticated && creditAmount != undefined && (
          <IonList lines="none" className="splitList">
            <IonItem>
              <IonLabel>
                {t("split_payments.your_credit", { name: paymentName })} {getPrice(creditAmount, tenant.settings?.fidelityAccount?.account.currency)}
              </IonLabel>
            </IonItem>

            <IonButton onClick={logoutAndRedirect} expand="block">
              {t("table_payment.changeUser")}
            </IonButton>

            <IonItem>
              <IonLabel>{t("split_payments.identify_payer")}</IonLabel>
            </IonItem>
            <IonItem>
              <IonInput placeholder="Nome" value={paymentName} onIonInput={(e) => setPaymentName(e.detail.value!)}></IonInput>
            </IonItem>
            <IonItem>
              <IonLabel>{t("split_payments.enter_quota")}</IonLabel>
            </IonItem>
            <IonItem>
              <div className="amountContainer">
                <IonInput class="custom" inputmode="decimal" type="number" value={paymentAmount} onIonInput={(e) => formatAmount(e.detail.value)} ref={amountInput}></IonInput>
              </div>
            </IonItem>
            <IonItem>
              <div className="amountTotalBtn" onClick={() => setPaymentAmount(Math.min(totalAmount, maxPaymentAmount))}>
                {t("split_payments.missing_amount")} {getPrice(totalAmount, tenant.settings?.fidelityAccount?.account.currency)}
              </div>
            </IonItem>
            <IonButton disabled={paymentAmount === 0 || paymentName === undefined || paymentAmount === undefined || paymentName === ""} onClick={confirm} expand="block">
              {t("table_payment.confirm")}
            </IonButton>
          </IonList>
        )}
        {!paymentWithCredit && (
          <IonList lines="none" className="splitList">
            <IonItem>
              <IonLabel>{t("split_payments.identify_payer")}</IonLabel>
            </IonItem>
            <IonItem>
              <IonInput placeholder="Nome" value={paymentName} onIonInput={(e) => setPaymentName(e.detail.value!)}></IonInput>
            </IonItem>
            <IonItem>
              <IonLabel>{t("split_payments.enter_quota")}</IonLabel>
            </IonItem>
            <IonItem>
              <div className="amountContainer">
                <IonInput class="custom" inputmode="decimal" type="number" value={paymentAmount} onIonInput={(e) => formatAmount(e.detail.value)} ref={amountInput}></IonInput>
              </div>
            </IonItem>
            <IonItem>
              <div className="amountTotalBtn" onClick={() => setPaymentAmount(Math.min(totalAmount, maxPaymentAmount))}>
                {t("split_payments.missing_amount")} {getPrice(totalAmount, tenant.settings?.fidelityAccount?.account.currency)}
              </div>
            </IonItem>
            <IonButton disabled={paymentAmount === 0 || paymentName === undefined || paymentAmount === undefined || paymentName === ""} onClick={confirm} expand="block">
              {t("table_payment.confirm")}
            </IonButton>
          </IonList>
        )}
        {paymentWithCredit && !auth.authenticated && (
          <form className="form" onSubmit={handleSubmit(onSubmit)}>
            <IonGrid className="payment-auth">
              <IonRow>
                <IonCol size="12">
                  <IonItem className={errors.email ? "error" : ""}>
                    <IonLabel position="floating">{t("auth.email")}</IonLabel>
                    <Controller name="email" control={control} render={({ field }) => <IonInput onIonInput={field.onChange} onIonBlur={field.onBlur} />} />
                  </IonItem>
                  {errors.email && <IonNote color="danger">{errors.email?.message}</IonNote>}
                </IonCol>
                <IonCol size="12">
                  <div className={"passwordContainer"}>
                    <IonItem className={errors.password ? "error inputPassword" : "inputPassword"}>
                      <IonLabel position="floating">{t("auth.password")}</IonLabel>
                      <Controller name="password" control={control} render={({ field }) => <IonInput type={showPassword ? "text" : "password"} onIonInput={field.onChange} onIonBlur={field.onBlur}></IonInput>} />
                    </IonItem>
                    <IonButton className="show-password" fill="clear" onClick={() => setShowPassword(!showPassword)}>
                      <IonIcon icon={showPassword ? eyeOff : eye} />
                    </IonButton>
                  </div>
                  {errors.password && <IonNote color="danger">{errors.password?.message}</IonNote>}
                </IonCol>
              </IonRow>
              <IonRow className="form-buttons">
                <IonCol size="12">
                  <IonButton type="submit" color="primary">
                    {t("auth.login")}
                  </IonButton>
                </IonCol>
              </IonRow>
            </IonGrid>
          </form>
        )}
      <IonAlert
        header={t("split_payments.insufficient_credit_alert_title")}
        message={t("split_payments.insufficient_credit_alert_message")}
        isOpen={showNotEnoughCreditAlert}
        onDidDismiss={() => setShowNotEnoughCreditAlert(false)}
        buttons={[
          {
            text: t("split_payments.insufficient_credit_alert_confirm"),
            role: "confirm"
          },
          {
            text: t("split_payments.insufficient_credit_alert_cancel"),
            role: "cancel",
            handler: close,
          },
        ]}
      ></IonAlert>
      </IonContent>
    </IonPage>
  );
};

export default SplitPaymentsModal;
